import { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { Box, Paper, Stepper, Step, StepLabel, Button, Typography, Snackbar, Alert, FormControlLabel, Switch, IconButton, Divider } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { getCurrentFYear } from '../../utils/financialYear';
import ClientDetails from './components/ClientDetails';
import ProductDetails from './components/ProductDetails';
import ConsignerDetails from './components/ConsignerDetails'; // Renamed from ConsignerDetails
import TransporterDetails from './components/TransporterDetails';
import ExportDetails from './components/ExportDetails';
import InquiryReview from './components/InquiryReview';
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import axios from 'axios';

const steps = ['Client Details', 'Product Details',
    // 'Consignor & Consignee', 
    'Dispatch Details',
    // 'Export Details', 
    'Review & Submit'];

const ChallanMaster = ({ mode, connectionString, onPopupCancel }) => {
    const { user, selectedCompany, loading } = useAuth();
    const userId = user?.UserId || null;
    const companyId = selectedCompany?.CompId || null;
    const AppRegID = selectedCompany?.AppRegID || null;
    const fYear = getCurrentFYear();
    const API_URL = `${connectionString}/api`;
    const Navigate = useNavigate();
    const navigate = useNavigate();
    const { clientId } = useParams(); // Extract clientId from URL parameters
    const { id } = useParams();
    const location = useLocation(); // Get navigation state
    const [activeStep, setActiveStep] = useState(0);
    const [formData, setFormData] = useState({});
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [consignorEnabled, setConsignorEnabled] = useState(true); // Renamed from consignerEnabled
    const [transporterEnabled, setTransporterEnabled] = useState(true);
    const [exportEnabled, setExportEnabled] = useState(false);
    const [selectedSOIDs, setSelectedSOIDs] = useState(location.state?.selectedSOIDs || []); // Initialize with navigation state
    const [isInvoiceLocked, setIsInvoiceLocked] = useState(false);

    const disablePartyName = Array.isArray(formData?.product?.tableData) && formData.product.tableData.some(row => row?.SOID != null || row?.SODtsID != null);

    const isFinalLocked = isInvoiceLocked || disablePartyName;
    // console.log(companyId)
    let challanId = id;

    // console.log("formDATA", formData)

    // Fetch or initialize sales order data for the client
    useEffect(() => {
        const fetchSalesOrders = async () => {
            try {
                // console.log('ChallanMaster (ref mode) - Received data:', { clientId, selectedSOIDs });
                const response = await axios.post(`${API_URL}/orders-admin/${clientId}`, { selectedSOIDs });
                const data = response.data;
                console.log('ChallanMaster (ref mode) - API response from /orders-admin:', data);
                setFormData(data);
            } catch (error) {
                console.error('Error fetching sales orders:', error);
                setSnackbar({ open: true, message: 'Failed to fetch sales order data.', severity: 'error' });
            }
        };

        if (clientId && selectedSOIDs.length > 0 && mode === 'ref') {
            fetchSalesOrders();
        }
    }, [clientId, selectedSOIDs, mode]);// Run when clientId, selectedSOIDs, or mode changes

    // / ─── NEW: Fetch existing challan when editing by challanId
    useEffect(() => {
        const fetchChallan = async () => {
            const editClientId = location.state?.clientId;
            if (!editClientId) {
                console.error('No clientId provided for edit mode');
                setSnackbar({ open: true, message: 'Client ID not found for this challan.', severity: 'error' });
                return;
            }

            try {
                const res = await axios.post(`${API_URL}/challans`, {
                    clientId: editClientId,
                    challanIds: [Number(challanId)]
                });
                const data = res.data;
                console.log('Fetched challan (edit):', data);
                setFormData(data);
                // Set enabled states based on whether fields are null
                setConsignorEnabled(data.consigner !== null);
                setTransporterEnabled(data.transporter !== null || data.consigner !== null);
                setExportEnabled(data.export !== null);
                // setSnackbar({ open: true, message: 'Challan data fetched successfully!', severity: 'success', });
            } catch (err) {
                console.error('Failed to load challan', err);
                if (err.response && err.response.status === 404) {
                    setSnackbar({ open: true, message: 'Challan not found for the given ID and client.', severity: 'error' });
                } else {
                    setSnackbar({ open: true, message: 'Failed to load Challan', severity: 'error' });
                }
            }
        };
        // console.log(mode);
        // Only run when we are in edit mode and challanId exists
        if (mode === 'edit' && challanId) {
            fetchChallan();
        }
    }, [mode, challanId, location.state?.clientId]);

    useEffect(() => {
        if (mode !== 'edit') return;

        const challanDtsIds =
            Array.isArray(formData?.product?.tableData)
                ? formData.product.tableData
                    .map(r => r.challanDtsID)
                    .filter(Boolean)
                : [];

        if (challanDtsIds.length === 0) return;

        const checkInvoiceLock = async () => {
            try {
                const res = await axios.post(
                    `${API_URL}/check-challan-invoice-lock`,
                    { challanDtsIds }
                );

                if (res.data?.locked) {
                    setIsInvoiceLocked(true);
                }
            } catch (err) {
                console.error('Invoice lock check failed', err);
            }
        };

        checkInvoiceLock();
    }, [mode, formData?.product?.tableData, API_URL]);


    const deepMerge = (target, source) => {
        const output = { ...target };
        for (const key in source) {
            if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
                output[key] = deepMerge(target[key] || {}, source[key]);
            } else {
                output[key] = source[key];
            }
        }
        return output;
    };

    const handleNext = (stepData = {}) => {
        // console.log('Step data:', stepData);
        setFormData(prev => deepMerge(prev, stepData));
        setActiveStep(prev => prev + 1);
    };

    const handleBack = () => setActiveStep(prev => prev - 1);

    const handleSubmit = async () => {
        try {
            const payload = {
                client: formData.client,
                product: formData.product,
                consigner: transporterEnabled ? formData.consigner : null, // Renamed from consigner
                transporter: transporterEnabled ? formData.transporter : null,
                export: exportEnabled ? formData.export : null,
                companyId, userId, clientId,
                AppRegID,
                fYear,
                selectedSOIDs, // Include updated selectedSOIDs
                mode
            };

            console.log(mode, 'Submitting payload:', payload);
            if (mode === 'add' || mode === 'ref') {
                await axios.post(`${API_URL}/sale-challan-v2`, payload);
            }
            if (mode === 'edit') {
                // console.log('>>>>', challanId);
                await axios.put(`${API_URL}/update-challan/${challanId}`, payload);
            }

            setSnackbar({ open: true, message: mode === 'add' ? 'Challan created successfully!' : 'Challan updated successfully!', severity: 'success', });

            setActiveStep(0);
            setFormData({});
            setSelectedSOIDs([]); // Reset after submission
            setTimeout(() => {
                if (typeof onPopupCancel === "function") onPopupCancel();
                else Navigate(-1);
            }, 600);
        } catch (error) {
            console.error('Submission error:', error);
            setSnackbar({ open: true, message: 'Failed to submit challan.', severity: 'error', });
        }
    };

    const handleCancel = () => {
        if (typeof onPopupCancel === "function") {
            onPopupCancel();
        } else {
            navigate(-1);
        }
    };

    // Sync activeStep with URL changes (e.g., browser back button).
    // Note: by the time popstate fires, the browser has ALREADY moved history
    // back one entry — calling handleCancel()/navigate(-1) here would pop a
    // SECOND entry on top of that, overshooting past the intended page. Just
    // reset local wizard state; the browser's own navigation handles the page.
    useEffect(() => {
        const handlePopState = () => {
            setActiveStep(0);
        };
        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, []);

    // ──────────────── Alt+C (Cancel) shortcut — works from any step ───────────
    // Attached here (the wizard's parent), not per-step, so it works regardless
    // of which step (Client Details / Product Details / Dispatch Details /
    // Review) is active — those step components don't each carry their own
    // cancel concept.
    // Attached to document in the capture phase so it fires regardless of which
    // field/dropdown currently has focus, including MUI popper/portal content.
    // Uses e.code (physical key position) instead of e.key so this also works on
    // macOS, where Option+letter often produces an accented/dead-key character
    // via e.key (e.g. Option+C → "ç") instead of the plain letter.
    useEffect(() => {
        const handleGlobalKeyDown = (e) => {
            if (!e.altKey || e.code !== "KeyC") return;
            e.preventDefault();
            handleCancel();
        };

        document.addEventListener("keydown", handleGlobalKeyDown, true);
        return () => document.removeEventListener("keydown", handleGlobalKeyDown, true);
    }, [handleCancel]);

    const handleCloseSnackbar = () => setSnackbar(prev => ({ ...prev, open: false }));

    const renderStepHeader = (icon, title, control = null) => (
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Box display="flex" alignItems="center">
                {activeStep > 0 && (
                    <IconButton onClick={handleBack} size="small" sx={{ mr: 1 }}>
                        <ArrowBack />
                    </IconButton>
                )}
                <Typography variant="h6" fontWeight="bold">{icon} {title}</Typography>
            </Box>
            {control}
        </Box>
    );

    const getStepComponent = (step) => {
        switch (step) {
            case 0:
                return (
                    <>
                        {renderStepHeader('1️⃣', 'Client Details')}
                        <ClientDetails
                            mode={mode}
                            initialData={{ ...formData.client, clientId: clientId || formData.client?.clientId }}
                            onNext={(data) => handleNext({ client: data })}
                            connectionString={connectionString}
                            disablePartyName={isFinalLocked}
                        />
                    </>
                );

            case 1:
                return (
                    <>
                        {renderStepHeader('2️⃣', 'Product Details')}
                        <ProductDetails
                            initialData={formData.product || {}} onNext={(data) => handleNext({ product: data })} clientData={formData.client || {}} L_No={formData.L_No || null} connectionString={connectionString} mode={mode} isOrderLocked={isFinalLocked}
                            onRowsChange={(rows) => {
                                setFormData(prev => ({
                                    ...prev,
                                    product: {
                                        ...(prev.product || {}),
                                        tableData: rows,
                                    }
                                }));
                            }}
                        />
                    </>
                );

            // case 2:
            //     return (
            //         <>
            //             {renderStepHeader('3️⃣', 'Consignor & Consignee', (<FormControlLabel control={<Switch checked={consignorEnabled} onChange={e => setConsignorEnabled(e.target.checked)} />} label="Enable" />))}
            //             {consignorEnabled ? (
            //                 <ConsignerDetails mode={mode} clientData={formData.client || {}} initialData={formData.consigner || {}} onNext={data => handleNext({ consigner: data })} onBack={handleBack} connectionString={connectionString} />
            //             ) : (
            //                 <Box mt={2}>
            //                     <Typography color="text.secondary">Consignor details are skipped.</Typography>
            //                     <Button variant="contained" onClick={() => handleNext({})}>Continue</Button>
            //                 </Box>
            //             )}
            //         </>
            //     );

            /* COMBINED STEP (Step 2 consignee and despatch details) */
            case 2:
                return (
                    <>
                        {renderStepHeader('3️⃣', 'Dispatch Details', (<FormControlLabel control={<Switch checked={transporterEnabled} onChange={e => setTransporterEnabled(e.target.checked)} />} label="Enable" />))}
                        {transporterEnabled ? (
                            <>
                                <Box sx={{ display: 'flex', flexDirection: "column", gap: 4 }}>
                                    <ConsignerDetails
                                        clientData={formData.client || {}}
                                        initialData={formData.consigner}
                                        connectionString={connectionString}
                                        onChange={(data) =>
                                            setFormData(prev => ({ ...prev, consigner: data }))
                                        }
                                    />

                                    <TransporterDetails
                                        initialData={formData.transporter || {}}
                                        connectionString={connectionString}
                                        onChange={(data) =>
                                            setFormData(prev => ({
                                                ...prev,
                                                transporter: data
                                            }))
                                        }
                                    />
                                </Box>


                                {/* ✅ SINGLE NEXT BUTTON */}
                                <Box mt={3} display="flex" justifyContent="flex-end">
                                    <Button
                                        variant="contained"
                                        // disabled={
                                        //     !formData?.consigner?.shipFrom ||
                                        //     !formData?.consigner?.shipTo ||
                                        //     !formData?.transporter?.transporterName
                                        // }
                                        onClick={() => {
                                            handleNext({
                                                consigner: formData.consigner,
                                                transporter: formData.transporter
                                            });
                                        }}
                                    >
                                        Next
                                    </Button>
                                </Box>

                            </>
                        ) : (
                            <Box mt={2}>
                                <Typography color="text.secondary">Dispatch details are skipped.</Typography>
                                <Button variant="contained" onClick={() => handleNext({})}>Continue</Button>
                            </Box>
                        )}

                    </>
                );


            // case 3:
            //     return (
            //         <>
            //             {renderStepHeader('4️⃣', 'Dispatch Details', (<FormControlLabel control={<Switch checked={transporterEnabled} onChange={e => setTransporterEnabled(e.target.checked)} />} label="Enable" />))}
            //             {transporterEnabled ? (
            //                 <TransporterDetails mode={mode} clientData={formData.client || {}} initialData={formData.transporter || {}} onNext={data => handleNext({ transporter: data })} onBack={handleBack} connectionString={connectionString} />
            //             ) : (
            //                 <Box mt={2}>
            //                     <Typography color="text.secondary">Dispatch details are skipped.</Typography>
            //                     <Button variant="contained" onClick={() => handleNext({})}>Continue</Button>
            //                 </Box>
            //             )}
            //         </>
            //     );

            // case 4:
            //     return (
            //         <>
            //             {renderStepHeader('5️⃣', 'Export Details', (<FormControlLabel control={<Switch checked={exportEnabled} onChange={e => setExportEnabled(e.target.checked)} />} label="Enable" />))}
            //             {exportEnabled ? (
            //                 <ExportDetails mode={mode} initialData={formData.export || {}} onNext={data => handleNext({ export: data })} onBack={handleBack} connectionString={connectionString} />
            //             ) : (
            //                 <Box mt={2}>
            //                     <Typography color="text.secondary">Export details are skipped.</Typography>
            //                     <Button variant="contained" onClick={() => handleNext({})}>Continue</Button>
            //                 </Box>
            //             )}
            //         </>
            //     );

            case 3:
                return ( // Include selectedSOIDs in review
                    <InquiryReview formData={{ ...formData, selectedSOIDs }} selectedCompany={selectedCompany} onBack={handleBack} onSubmit={handleSubmit} onEditStep={(stepIndex) => setActiveStep(stepIndex)} connectionString={connectionString} mode={mode} isInvoiceLocked={isInvoiceLocked} />
                );

            default: return null;
        }
    };

    return (
        <Box maxWidth="lg" mx="auto" px={2} py={3} pb={0} >
            {/* <Typography variant="h5" sx={{ textAlign: 'center', fontSize: { xs: '1.25rem', sm: '1.5rem' }, fontWeight: 600, mt: -1.5, mb: 1 }}>
                {mode === 'edit' ? 'Edit Challan' : 'Add New Challan'}
            </Typography> */}

            <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 0.6, mt: -1.5 }}>
                {/* Back arrow — mobile/tablet only */}
                <IconButton onClick={() => navigate(-1)} sx={{ position: 'absolute', left: 0, display: { xs: 'flex', md: 'none' }, color: 'text.secondary', p: 0.5, }} >
                    <ArrowBackIcon fontSize="small" />
                </IconButton>

                <Typography variant="h5" sx={{ textAlign: 'center', fontSize: { xs: '1.25rem', sm: '1.5rem' }, fontWeight: 600 }}>
                    {mode === 'edit' ? 'Edit Challan' : 'Add New Challan'}
                </Typography>
            </Box>
            <Paper elevation={4} sx={{ borderRadius: 3, p: 1.5 }}>
                {isInvoiceLocked && (
                    <Alert severity="warning" sx={{ mb: 1 }}>
                        Challan is locked. Client Details and Product Details cannot be modified.
                    </Alert>
                )}
                <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 1 }}>
                    {steps.map(label => (
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>
                <Divider sx={{ mb: 0.5 }} />
                {getStepComponent(activeStep)}
            </Paper>
            <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} variant="filled">
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default ChallanMaster;